import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import PropTypes from 'prop-types';
// Models
import mProfile from 'models/profile';
import mPermission from 'models/permission';
// Custom
import {
  openDialog, getPermissions, getProfilesList, deleteProfile, refreshList,
} from 'actions';
import { fullListShape, deleteShape } from 'constants/PropTypesShapes';
import { computeErrors } from 'utils/Forms';
import { addPath, navigateToShow } from 'profiles/navigation';
import { AddTextButton } from 'components/buttons/TextButtons';
import MainListTemplate from 'components/templates/MainListTemplate';
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withLoading from 'hocs/WithLoading';
import withStateReinit from 'hocs/WithStateReinit';
import withTitle from 'hocs/WithTitle';
import ProfileFilters from 'profiles/assets/main/ProfileFilters';
import ProfileRow from 'profiles/assets/main/ProfileRow';

const ProfilesList = ({
  profiles,
  profileDelete,

  handleDisplayConfirm,
  handleStartLoading,

  dispatchRefreshList,
  dispatchOpenDialog,
  dispatchGetProfilesList,
  dispatchGetPermissions,
  dispatchDeleteProfile,
}) => {
  const errors = computeErrors(null, profileDelete.error);
  /* ========================================================= */
  /* EFFECTS AND HANDLERS                                      */
  /* ========================================================= */

  React.useEffect(function retrievePermissions() {
    dispatchGetPermissions();
  }, []);

  React.useEffect(function refreshListAfterDeletingProfile() {
    if (profileDelete.deleted) {
      dispatchRefreshList();
    }
  }, [profileDelete.deleted]);

  const handleDisplayPermissions = (profile) => {
    const DIALOG_PROPERTIES = {
      title: {
        id: 'title.profilePermissions',
        values: { profileName: profile.name },
      },
      actions: [
        { type: 'close' },
      ],
      component: {
        type: 'PROFILE_PERMISSIONS',
        props: {
          profile,
        },
      },
      size: 'md',
    };

    dispatchOpenDialog(DIALOG_PROPERTIES);
  };

  const handleDeleteProfile = (profile) => {
    handleDisplayConfirm(
      { id: 'confirm.deleteProfile', values: { profileName: profile.name } },
      () => dispatchDeleteProfile(profile.id),
    );
  };

  const handleRowDbleClick = (profile) => {
    if (profile) {
      const profileIsBookingsType = mPermission.hasOnlyBookingsPermission(profile.permissions);
      // As we must protect the unique profile of type 'bookings' we disable the ability to access to it and edit it
      if (!profileIsBookingsType) {
        // Display loader and display profile
        handleStartLoading();
        setTimeout(() => navigateToShow(profile.id), 500);
      }
    }
  };

  const doGetRowComp = (rowProps) => (
    <ProfileRow
      {...rowProps}
      onDisplayPermissions={handleDisplayPermissions}
      onDelete={handleDeleteProfile}
    />
  );

  return (
    <MainListTemplate
      title={<LocalizedMessage id="title.profilesList" />}
      icon={mProfile.icon}
      displayRefresh
      actionButtons={[
        <AddTextButton key="add" primary path={addPath()} />,
      ]}
      errors={errors}
      name={mProfile.name}
      listFeatures={mProfile.listFeatures}
      withActions
      nbActions={2}
      elementsData={{
        loading: profiles.loading,
        elements: profiles.elements,
        handleGetList: dispatchGetProfilesList,
      }}
      onRowDbleClick={handleRowDbleClick}
      filtersComp={
        <ProfileFilters />
      }
      rowComp={doGetRowComp}
    />
  );
};

ProfilesList.propTypes = {
  profiles: fullListShape.isRequired,
  profileDelete: deleteShape.isRequired,

  handleDisplayConfirm: PropTypes.func.isRequired,
  handleStartLoading: PropTypes.func.isRequired,

  dispatchRefreshList: PropTypes.func.isRequired,
  dispatchOpenDialog: PropTypes.func.isRequired,
  dispatchGetPermissions: PropTypes.func.isRequired,
  dispatchGetProfilesList: PropTypes.func.isRequired,
  dispatchDeleteProfile: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  profiles: state.profile.list,
  profileDelete: state.profile.delete,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchRefreshList: () => {
    dispatch(refreshList());
  },
  dispatchOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
  dispatchGetPermissions: () => {
    dispatch(getPermissions());
  },
  dispatchGetProfilesList: (pagination, sort, filters) => {
    dispatch(getProfilesList(pagination, sort, filters));
  },
  dispatchDeleteProfile: (id) => {
    dispatch(deleteProfile(id));
  },
});

module.exports = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withConfirmDialog,
  withLoading,
  withStateReinit(['deleteProfile']),
  withTitle('pageTitle.profiles'),
)(ProfilesList);



// WEBPACK FOOTER //
// ./routes/app/routes/profiles/routes/list/components/ProfilesList.js